import {
  CLIENT_TICK_MS,
  LERP,
  SPECTATOR_FIT_MARGIN,
  SPECTATOR_MIN_VIEW_DIST,
  SPECTATOR_MOVE_SPEED,
  SPECTATOR_PICK_DEVICE_PX,
  SPECTATOR_PICK_MIN_WORLD,
  SPECTATOR_VIEW_DIST,
  SPECTATOR_ZOOM_STEP,
} from './constants/feel';
import type { PlayerEntity, World } from './world';
import type { Camera } from '../render/camera';

/**
 * The whole map fits when the *limiting* axis fits. `targetScale` divides by the longer viewport
 * edge, so a view distance equal to the map's width shows the full width and only a slice of the
 * height on a landscape monitor.
 */
export function fitViewDist(camera: Camera, world: World): number {
  const longEdge = Math.max(camera.deviceWidth, camera.deviceHeight);
  const perAxis = Math.max(
    world.world.maxX / camera.deviceWidth,
    world.world.maxY / camera.deviceHeight,
  );
  return longEdge * perAxis * SPECTATOR_FIT_MARGIN;
}

function clampCentre(value: number, span: number, max: number): number {
  if (span >= max) return max / 2;
  return Math.min(Math.max(value, span / 2), max - span / 2);
}

export class SpectatorCamera {
  viewDist = SPECTATOR_VIEW_DIST;

  followId: number | null = null;

  reset(): void {
    this.viewDist = SPECTATOR_VIEW_DIST;
    this.followId = null;
  }

  /** The world point under the cursor keeps its screen position, measured against the scale being eased *to* — easing against the current one drifts the anchor by however far the ease has left to run. A followed ship is the anchor instead, and survives the zoom: tightening on the fight being watched is the obvious thing to do while watching it. */
  zoomBy(steps: number, deviceX: number, deviceY: number, camera: Camera, world: World): void {
    const before = camera.targetScale;
    this.viewDist = Math.min(
      fitViewDist(camera, world),
      Math.max(SPECTATOR_MIN_VIEW_DIST, this.viewDist * Math.pow(SPECTATOR_ZOOM_STEP, steps)),
    );
    camera.freeViewDist = this.viewDist;
    const after = camera.targetScale;
    if (before <= 0 || after <= 0) return;

    if (this.followId === null) {
      const shift = 1 / before - 1 / after;
      world.selfX += (deviceX - camera.deviceWidth / 2) * shift;
      world.selfY += (deviceY - camera.deviceHeight / 2) * shift;
    }
    this.clamp(camera, world);
  }

  seekTo(x: number, y: number, camera: Camera, world: World): void {
    this.followId = null;
    world.selfX = x;
    world.selfY = y;
    this.clamp(camera, world);
  }

  /** A click that lands on nobody is how following is let go of, so an empty patch of space is a meaningful target. */
  clickAt(worldX: number, worldY: number, camera: Camera, world: World): void {
    const reach = Math.max(
      SPECTATOR_PICK_MIN_WORLD,
      SPECTATOR_PICK_DEVICE_PX / Math.max(camera.worldScale, 1e-6),
    );
    let best: PlayerEntity | null = null;
    let bestDistance = reach * reach;
    for (const entity of world.entities.values()) {
      if (entity.kind !== 'player') continue;
      const dx = entity.displayX - worldX;
      const dy = entity.displayY - worldY;
      const distance = dx * dx + dy * dy;
      if (distance > bestDistance) continue;
      bestDistance = distance;
      best = entity as PlayerEntity;
    }
    this.followId = best === null ? null : best.id;
  }

  followedName(world: World): string | null {
    if (this.followId === null) return null;
    const entity = world.entities.get(this.followId);
    if (entity === undefined || entity.kind !== 'player') return null;
    return world.nameCache.get((entity as PlayerEntity).nameId) ?? null;
  }

  tick(camera: Camera, world: World, panAxes: readonly [boolean, boolean, boolean, boolean]): void {
    camera.freeViewDist = Math.min(this.viewDist, fitViewDist(camera, world));
    this.viewDist = camera.freeViewDist;

    const [left, up, right, down] = panAxes;
    if (left || up || right || down) {
      this.followId = null;
      const speed = SPECTATOR_MOVE_SPEED * (this.viewDist / SPECTATOR_VIEW_DIST);
      const step = (speed * CLIENT_TICK_MS) / 1000;
      if (left) world.selfX -= step;
      if (right) world.selfX += step;
      if (up) world.selfY -= step;
      if (down) world.selfY += step;
    } else if (this.followId !== null) {
      const entity = world.entities.get(this.followId);
      if (entity === undefined) this.followId = null;
      else {
        world.selfX += (entity.displayX - world.selfX) * LERP.spectatorFollow;
        world.selfY += (entity.displayY - world.selfY) * LERP.spectatorFollow;
      }
    }

    this.clamp(camera, world);
    world.spectatorFollowId = this.followId;
  }

  private clamp(camera: Camera, world: World): void {
    const scale = camera.targetScale;
    if (scale <= 0) return;
    world.selfX = clampCentre(world.selfX, camera.deviceWidth / scale, world.world.maxX);
    world.selfY = clampCentre(world.selfY, camera.deviceHeight / scale, world.world.maxY);
  }
}
